import { Component, DestroyRef, EventEmitter, inject, Input, OnChanges, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ProductsService } from 'src/app/services/products.service';
import { Observable, Subject, takeUntil } from 'rxjs';
import { AuthService } from 'src/app/services/auth.service';
import { Permission } from 'src/app/constants/permission.constant';
import { SignalRAssetGroups, ReviewableAssetType } from 'src/app/constants/signalr.constant';
import { EAssetType, EChannel, IAsset, EStateAsset } from 'src/app/models/products/asset';
import { SignalrService } from 'src/app/services/signalr.service';
import { SignalrGroup } from 'src/app/services/signalr-group';
import { AlertService } from 'src/app/services/alert.service';
import { MatDialog } from '@angular/material/dialog';
import { WarningModalComponent } from 'src/app/components/shared/warning-modal/warning-modal.component';
import { ProblemDetails } from 'src/app/models/problem-details';

@Component({
    selector: 'app-review-actions',
    templateUrl: './review-actions.component.html',
    styleUrls: ['./review-actions.component.scss'],
    changeDetection: ChangeDetectionStrategy.Default,
    standalone: false
})
export class ReviewActionsComponent implements OnInit, OnChanges {
  @Input() disabled!: boolean;
  @Input() assetDetail!: IAsset;
  @Output() onReload = new EventEmitter();
  assetState: EStateAsset = EStateAsset.IN_REVIEW;
  eState = EStateAsset;
  textMapping: any = {
    [EStateAsset.IN_REVIEW]: 'Approve',
    [EStateAsset.APPROVED]: 'Approved',
    [EStateAsset.REJECTED]: 'Rejected',
  };
  currentChannel: EChannel = EChannel.EXTERNAL;
  Permission = Permission;
  private readonly destroyRef = inject(DestroyRef);
  private destroy$ = new Subject();
  assetType = 'shoot-asset';

  constructor(
    private signalrService: SignalrService,
    private productService: ProductsService,
    public authService: AuthService,
    private route: ActivatedRoute,
    public dialog: MatDialog,
    private alertService: AlertService,
  ) {}

  ngOnInit(): void {
    this.initialData();
    this.subscribeToChannelChanges();
    this.subscribeToSignalRConnection();
    this.subscribeToAssetUpdates();
  }

  ngOnChanges(): void {
    this.initialData();
  }

  initialData(): void {
    this.assetType = this.readAssetType();
    this.assetState = this.resolveAssetState(this.assetDetail);
  }

  updateAssetState(data: any): void {
    this.assetState = this.resolveAssetState(data);
  }

  checkReject(): void {
    if (this.requiresEnhancedWarning()) {
      this.confirmEnhancedReject();
    } else {
      this.onReview(this.eState.REJECTED);
    }
  }

  onReview(state: EStateAsset | null): void {
    const lastState = this.assetState;
    this.assetState = this.toDisplayState(state);

    this.reviewAsset$(state)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.onReload.emit(),
        error: (error) => this.revertWithError(lastState, error),
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
    this.leaveGroup(this.currentChannel);
    this.signalrService.clearAsset();
  }

  get hasReviewPermission(): boolean {
    if (this.assetType === EAssetType.SHOOT_ASSETS) {
      return this.authService.hasPermission(Permission.ShootAssetsReview);
    }
    return this.currentChannel === EChannel.INTERNAL
      ? this.authService.hasPermission(Permission.PostAssetsInternalReview)
      : this.authService.hasPermission(Permission.PostAssetsExternalReview);
  }

  private readAssetType(): string {
    return (
      this.route.snapshot.paramMap.get('assetType') ??
      (this.route.snapshot.data?.['assetType'] as string | undefined) ??
      ''
    );
  }

  private isReviewableAssetType(): boolean {
    return (
      this.assetType === EAssetType.SHOOT_ASSETS ||
      this.assetType === EAssetType.POST_ASSETS
    );
  }

  private resolveAssetState(data: any): EStateAsset {
    const state = this.currentChannel === EChannel.EXTERNAL
      ? data.clientReviewState
      : data.internalReviewState;
    return state || EStateAsset.IN_REVIEW;
  }

  private toDisplayState(state: EStateAsset | null): EStateAsset {
    return state ?? this.eState.IN_REVIEW;
  }

  private requiresEnhancedWarning(): boolean {
    return this.assetType === EAssetType.SHOOT_ASSETS && !!this.assetDetail.isEnhanced;
  }

  private confirmEnhancedReject(): void {
    const dialogRef = this.dialog.open(WarningModalComponent, {
      data: {
        header: 'Warning',
        content:
          'This shoot asset has been enhanced, rejecting will also mark any post assets as rejected',
        titleButtonOk: 'Continue',
        titleButtonNo: 'Cancel',
        showBtnNo: true,
      },
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.onReview(this.eState.REJECTED);
      }
    });
  }

  private revertWithError(previousState: EStateAsset, error: any): void {
    this.assetState = previousState;
    this.alertService.errorToast(this.extractErrorMessage(error));
  }

  private subscribeToChannelChanges(): void {
    if (!this.authService.hasPermission(Permission.PostAssetsPreRelease)) return;

    if (this.assetType == EAssetType.SHOOT_ASSETS) {
      this.productService.setPostAssetState(EChannel.INTERNAL);
    }
    this.productService.postAssetState.subscribe({
      next: (channel: EChannel) => this.switchChannel(channel),
    });
  }

  private switchChannel(channel: EChannel): void {
    this.leaveGroup(this.currentChannel);
    this.currentChannel = channel;
    this.joinGroup(channel);
    this.updateAssetState(this.assetDetail);
  }

  private subscribeToSignalRConnection(): void {
    SignalrGroup.subscribe({
      destroyRef: this.destroyRef,
      data$: this.signalrService.connectState,
      onData: (connected: boolean) => {
        if (connected) {
          this.joinGroup(this.currentChannel);
          this.productService.setConnectionId(this.signalrService.connectionId);
        }
      },
    });
  }

  private subscribeToAssetUpdates(): void {
    SignalrGroup.subscribe({
      destroyRef: this.destroyRef,
      data$: this.signalrService.assetData,
      onData: (data: IAsset) => {
        if (this.assetDetail.id === data.id) {
          this.updateAssetState(data);
          this.assetDetail = data;
        }
      },
    });
  }

  private reviewAsset$(state: EStateAsset | null): Observable<any> {
    if (this.assetType === EAssetType.SHOOT_ASSETS) {
      return this.productService.reviewShootAsset(this.assetDetail.id, state);
    }
    const isInternal = this.currentChannel === EChannel.INTERNAL;
    return this.productService.reviewPostAsset(this.assetDetail.id, isInternal, state);
  }

  private joinGroup(channel: EChannel): void {
    if (!this.isReviewableAssetType()) return;
    const hubMethod = SignalRAssetGroups[this.assetType as ReviewableAssetType].Review;
    if (this.assetType === EAssetType.SHOOT_ASSETS) {
      this.signalrService.joinIdGroup(hubMethod, this.assetDetail.id);
    } else {
      this.signalrService.joinChannelGroup(hubMethod, this.assetDetail.id, channel);
    }
  }

  private leaveGroup(channel: EChannel): void {
    if (!this.isReviewableAssetType()) return;
    const groupPath = this.buildGroupPath(channel);
    this.signalrService.leaveGroup(groupPath);
  }

  private buildGroupPath(channel: EChannel): string {
    return this.assetType === EAssetType.SHOOT_ASSETS
      ? `/assets/shoot/${this.assetDetail.id}/reviews`
      : `/assets/post/${this.assetDetail.id}/${channel.toLowerCase()}/reviews`;
  }

  private extractErrorMessage(error: any): string {
    const problem: ProblemDetails | undefined = error?.error;
    if (problem?.errors) {
      const firstMessage = Object.values(problem.errors).flat()[0];
      if (firstMessage) return firstMessage;
    }
    if (problem?.title && problem?.detail) {
      return `${problem.title}: ${problem.detail}`;
    }
    return problem?.title ?? 'Review failed';
  }
}
